import { CSSResultGroup, LitElement, html, nothing } from 'lit'
import { customElement, state, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './toast-host.style.js'
import { ifDefined } from 'lit/directives/if-defined.js'


export type ToastVariant = 'error' | 'info' | 'success' | 'warning'

export type ToastActionDetail = {
  text: string
  url?: string
  target?: '_self' | '_blank' | '_parent' | '_top'
  variant?: 'error' | 'info' | 'success' | 'warning'
  onClick?: () => void
}

export type ToastEventDetail = {
  variant: ToastVariant
  message: string
  action?: ToastActionDetail
  id?: string
}

type ToastQueueItem = Required<Pick<ToastEventDetail, 'variant' | 'message'>> & {
  action?: ToastActionDetail
  id: string
  isExiting?: boolean
}

export const TOAST_EVENT = 'csc:toast'

let toastId = 0

@customElement('csc-toast-host')
export class CSCToastHost extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @state()
  private toasts: ToastQueueItem[] = []

  private autoHideTimers = new Map<string, number>()

  @property({ type: Number, attribute: 'auto-hide-delay' })
  autoHideDelay = 3000

  private readonly handleToastEvent: EventListener = (event) => {
    const customEvent = event as CustomEvent<ToastEventDetail>

    if (!customEvent.detail?.message || !customEvent.detail.variant) {
      return
    }

    const item: ToastQueueItem = {
      id: customEvent.detail.id ?? `toast-${++toastId}`,
      message: customEvent.detail.message,
      variant: customEvent.detail.variant,
      ...(customEvent.detail.action ? { action: customEvent.detail.action } : {}),
    }

    this.clearAutoHide(item.id)
    const nonDuplicate = this.toasts.filter(toast => toast.id !== item.id)
    this.toasts = [item, ...nonDuplicate]
    this.startAutoHide(item.id)
    this.enforceVisibleLimit()
  }

  private removeToastImmediately(id: string) {
    this.clearAutoHide(id)
    this.toasts = this.toasts.filter(toast => toast.id !== id)
  }

  private readonly handleToastHide = (event: Event) => {
    const toastElement = event.currentTarget as HTMLElement | null
    const id = toastElement?.dataset['toastId']

    if (id) {
      this.startToastExit(id)
    }
  }

  public override connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener(TOAST_EVENT, this.handleToastEvent)
  }

  public override disconnectedCallback(): void {
    document.removeEventListener(TOAST_EVENT, this.handleToastEvent)
    this.clearAllAutoHide()
    super.disconnectedCallback()
  }

  protected override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(changedProperties)

    if (changedProperties.has('toasts')) {
      const previous = changedProperties.get('toasts') as ToastQueueItem[] | undefined
      const previousIds = new Set(previous?.map(toast => toast.id))

      this.toasts.forEach(toast => {
        if (!toast.isExiting && (!previousIds || !previousIds.has(toast.id))) {
          this.animateIn(toast.id)
        }
      })
    }
  }

  private enforceVisibleLimit() {
    const activeToasts = this.toasts.filter(toast => !toast.isExiting)

    if (activeToasts.length <= 3) {
      return
    }

    const overflow = activeToasts.slice(3)
    overflow.forEach(toast => this.startToastExit(toast.id))
  }

  private startToastExit(id: string) {
    const toast = this.toasts.find(item => item.id === id)

    if (!toast || toast.isExiting) {
      return
    }

    this.toasts = this.toasts.map(item => item.id === id ? { ...item, isExiting: true } : item)

    this.animateOut(id)
  }

  private startAutoHide(id: string) {
    const delay = Math.max(0, Number(this.autoHideDelay))
    const timer = window.setTimeout(() => {
      this.autoHideTimers.delete(id)
      this.startToastExit(id)
    }, delay)

    this.autoHideTimers.set(id, timer)
  }

  private clearAutoHide(id: string) {
    const timer = this.autoHideTimers.get(id)

    if (timer !== undefined) {
      window.clearTimeout(timer)
      this.autoHideTimers.delete(id)
    }
  }

  private clearAllAutoHide() {
    this.autoHideTimers.forEach(timer => window.clearTimeout(timer))
    this.autoHideTimers.clear()
  }

  private async animateIn(id: string) {
    await this.updateComplete
    const toastElement = this.getToastElement(id)

    if (!toastElement) {
      return
    }

    toastElement.animate([
      { opacity: 0, transform: 'translateX(40px) scale(0.95)' },
      { opacity: 1, transform: 'translateX(0) scale(1)' },
    ], {
      duration: 450,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards',
    })
  }

  private async animateOut(id: string) {
    await this.updateComplete
    const toastElement = this.getToastElement(id)

    if (!toastElement) {
      this.removeToastImmediately(id)
      return
    }

    const styles = getComputedStyle(toastElement)
    const initialHeight = `${toastElement.offsetHeight}px`

    toastElement.style.overflow = 'hidden'

    const animation = toastElement.animate([
      {
        opacity: 1,
        transform: 'translateX(0) scale(1)',
        height: initialHeight,
        marginTop: styles.marginTop,
        paddingTop: styles.paddingTop,
        paddingBottom: styles.paddingBottom,
      },
      {
        opacity: 0,
        transform: 'translateX(40px) scale(0.92)',
        height: '0px',
        marginTop: '0px',
        paddingTop: '0px',
        paddingBottom: '0px',
      },
    ], {
      duration: 350,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards',
    })

    await animation.finished.catch(() => undefined)
    this.removeToastImmediately(id)
    this.enforceVisibleLimit()
  }

  private getToastElement(id: string) {
    return this.renderRoot.querySelector<HTMLElement>(`.toast-item[data-toast-id="${id}"]`)
  }

  override render() {
    return html`
      <div class="toast-stack">
        ${this.toasts.map(toast => {
          const action = toast.action

          const handleActionClick = action?.onClick ? () => action.onClick?.() : undefined

          return html`
          <div class="toast-item ${toast.isExiting ? 'is-exiting' : ''}" data-toast-id="${toast.id}">
            <cse-toast
              data-toast-id="${toast.id}"
              variant="${toast.variant}"
              message="${toast.message}"
              @hide-toast=${this.handleToastHide}
            >
              ${toast.message}
              ${action ? html`
                <cse-chip
                  variant=${action.variant}
                  url=${ifDefined(action.url)}
                  target=${ifDefined(action.target)}
                  @click=${handleActionClick}
                >${action.text}
                </cse-chip>
              ` : nothing}
            </cse-toast>
          </div>
        `})}
      </div>
    `
  }
}

export const showToast = (detail: ToastEventDetail) => {
  document.dispatchEvent(new CustomEvent<ToastEventDetail>(TOAST_EVENT, {
    detail,
  }))
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-toast-host': CSCToastHost
  }
}

